import '@gl/lit/components/base/icon/index.js';
import '@gl/lit/components/base/floating_surface_v2/index.js';
import styles from './style.scss';

import type {CSSResultArray} from 'lit';
import {html} from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
} from 'lit/decorators.js';
import {ifDefined} from 'lit/directives/if-defined.js';

import {FlywheelComponent} from '@gl/lit/lib/component.js';
import {
  ImmutableComponent,
  modelGetter,
  modelSetter,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '@gl/lit/types/classes.js';
import {isFloatingList} from '@gl/lit/components/base/floating_surface_list/index.js';
import type {Model} from './architecture.js';
import {main, Msg} from './architecture.js';
import type {Placement} from '@floating-ui/dom';

export const COMPONENT_TAG = 'fly-dropdown-v2';

const propSetter = modelSetter<Msg>();

@customElement(COMPONENT_TAG)
export default class FlyDropdownV2 extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  @query('[data-anchor]')
  anchor?: HTMLElement;

  @property({type: Boolean})
  closeWhenClickOutside = false;

  @property({type: String})
  optionsPlacement?: string;

  static get styles(): CSSResultArray {
    return [styles];
  }

  @property({type: Boolean, reflect: true})
  get open() {
    return modelGetter(this, 'open');
  }
  set open(value: boolean) {
    const setter = propSetter(this, 'open');
    setter({value, msg: value ? Msg.open() : Msg.close()});
  }

  @property({type: Boolean, reflect: true})
  get disabled() {
    return modelGetter(this, 'disabled');
  }
  set disabled(value: boolean) {
    const setter = propSetter(this, 'disabled');
    setter({value, msg: value ? Msg.disable() : Msg.enable()});
  }

  @property({type: Boolean})
  updateOnAnimationFrame = false;

  @queryAssignedElements({slot: 'action'})
  private actionElements!: Array<HTMLElement>;

  @queryAssignedElements({slot: 'content'})
  private contentElements!: Array<HTMLElement>;

  private keyListenersUnsubscribe = () => {};
  private focusUnsubscribe = () => {};
  private blurUnsubscribe = () => {};

  private onActionSlotChange() {
    this.handleUpdate(
      this.actionElements?.length > 0
        ? Msg.registerActionButton()
        : Msg.removeActionButton()
    );
  }

  private startKeyListeners() {
    const abortController = new AbortController();
    document.addEventListener('keydown', this.handleKeydown.bind(this), {
      signal: abortController.signal,
    });
    this.keyListenersUnsubscribe = () => abortController.abort();
  }

  private stopKeyListeners() {
    this.keyListenersUnsubscribe();
  }

  private handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Tab':
        return this.handleUpdate(Msg.close());
      case 'ArrowDown': {
        event.preventDefault();
        return event.altKey && this.handleUpdate(Msg.open());
      }
      case 'ArrowUp': {
        event.preventDefault();
        return event.altKey && this.handleUpdate(Msg.close());
      }
      case 'Escape':
        return this.handleUpdate(Msg.close());
    }
  }

  beforeLoad(): void {
    const focusListener = this.startKeyListeners.bind(this);
    const blurListener = this.stopKeyListeners.bind(this);
    this.addEventListener('focus', focusListener);
    this.addEventListener('blur', blurListener);

    this.focusUnsubscribe = () => {
      this.removeEventListener('focus', focusListener);
    };
    this.blurUnsubscribe = () => {
      this.removeEventListener('blur', blurListener);
    };
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopKeyListeners();
    this.focusUnsubscribe();
    this.blurUnsubscribe();
  }

  toggle() {
    this.handleUpdate(this.open ? Msg.close() : Msg.open());
  }

  onOpen() {
    this.contentElements.forEach(element => {
      if (isFloatingList(element)) {
        element.active = true;
      }
      Array.from(element.children).forEach(element => {
        if (isFloatingList(element)) {
          element.active = true;
        }
      });
    });
  }

  onClose() {
    this.contentElements.forEach(element => {
      if (isFloatingList(element)) {
        element.active = false;
      }
      Array.from(element.children).forEach(element => {
        if (isFloatingList(element)) {
          element.active = true;
        }
      });
    });
  }

  handleSurfaceClose() {
    this.handleUpdate(Msg.close());
  }

  render() {
    return html`
      <div data-anchor part="dropdown-anchor" @click=${this.toggle}>
        <slot name="anchor"></slot>
      </div>

      <fly-floating-surface-v2
        part="dropdown-surface"
        placement=${ifDefined(this.optionsPlacement as Placement)}
        ?updateOnAnimationFrame=${this.updateOnAnimationFrame}
        .anchor=${this.anchor ?? null}
        ?closeWhenClickOutside=${this.closeWhenClickOutside}
        ?visible=${this.open && !this.disabled}
        @close=${this.handleSurfaceClose}
      >
        <slot name="content"></slot>
      </fly-floating-surface-v2>
    `;
  }
}

FlywheelComponent.tagRegistry.add(COMPONENT_TAG);

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyDropdownV2;
  }
}
